import { Link } from 'react-router-dom';
import { usePluginMenu } from './usePluginMenu';
import type { PluginSurface } from './types';

interface Props {
  surface: PluginSurface;
  /** Optional CSS class for each menu item — lets the host pass its
   * sidebar's link class so plugin entries match the surrounding style. */
  itemClassName?: string;
}

/**
 * Renders plugin-contributed menu items for the given surface.
 *
 * Place this component inside the host's sidebar at the position
 * where plugin entries should appear (typically below the built-in
 * nav). No loading spinner: plugins are rare + fast; the empty
 * initial state is invisible.
 *
 * Each entry is rendered as a `react-router-dom <Link>` so client-
 * side navigation works without a full page load.
 */
export function PluginMenuItems({ surface, itemClassName }: Props) {
  const { entries, error } = usePluginMenu(surface);
  if (error || entries.length === 0) {
    return null;
  }
  // The wrapper supplies the same horizontal gutter as the core nav; without
  // it plugin links sat flush against the sidebar edge, visibly unstyled.
  return (
    <div data-testid="plugin-menu" className="px-3 pb-2 space-y-0.5">
      {entries.map((entry) => (
        <Link
          key={`${entry.plugin_id}:${entry.path}`}
          to={entry.path}
          className={itemClassName}
          data-testid={`plugin-menu-${entry.plugin_id}`}
        >
          {entry.label}
        </Link>
      ))}
    </div>
  );
}
